import { useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { LIST_LIMIT_DEFAULT, listInput, type ListInput } from '@servwell/shared';
import type { ServerTable, Sort } from '@/components/ui/data-table';
import { useDebounced } from './useDebounced';

/** URL-owned table intent for Jobs/Quotes only. Legacy useServerTable stays local. */
export function workspaceTableSearch(raw: Record<string, unknown>) {
  const result: {
    search?: string;
    sort?: string;
    dir?: 'asc' | 'desc';
    filters?: Record<string, string>;
    page?: number;
  } = {};
  const search = listInput.shape.search.safeParse(raw.search);
  if (
    search.success &&
    search.data &&
    typeof raw.search === 'string' &&
    raw.search.length <= 120
  )
    result.search = raw.search;
  const sort = listInput.shape.sort.safeParse(raw.sort);
  if (sort.success && sort.data) {
    result.sort = sort.data;
    result.dir = raw.dir === 'desc' ? 'desc' : 'asc';
  }
  if (
    raw.filters &&
    typeof raw.filters === 'object' &&
    !Array.isArray(raw.filters) &&
    Object.keys(raw.filters).length <= 20
  ) {
    const parsed = listInput.shape.filters.safeParse(raw.filters);
    if (parsed.success && parsed.data) {
      const filters = Object.fromEntries(
        Object.entries(raw.filters as Record<string, string>).filter(
          ([, value]) => value.trim() !== '' && value.length <= 120,
        ),
      );
      if (Object.keys(filters).length) result.filters = filters;
    }
  }
  const page =
    typeof raw.page === 'number'
      ? raw.page
      : typeof raw.page === 'string' && /^\d+$/.test(raw.page)
        ? Number(raw.page)
        : 0;
  if (Number.isSafeInteger(page) && page > 0 && page <= 1_000_000) result.page = page;
  return result;
}

export function useWorkspaceTable(pageSize = LIST_LIMIT_DEFAULT) {
  const params: Record<string, unknown> = useSearch({ strict: false });
  const navigate = useNavigate();
  const state = workspaceTableSearch(params);
  const search = state.search ?? '';
  const settledSearch = useDebounced(search);
  const serializedFilters = JSON.stringify(state.filters ?? {});
  const settledFilters = useDebounced(serializedFilters);
  const filters = useMemo(
    () => JSON.parse(settledFilters) as Record<string, string>,
    [settledFilters],
  );
  const sort: Sort = state.sort ? { key: state.sort, dir: state.dir ?? 'asc' } : null;
  const page = state.page ?? 0;
  // Never combine an old debounced filter with a newly restored URL's later page.
  const effectivePage =
    settledSearch === search && settledFilters === serializedFilters ? page : 0;

  /** Filter changes reset the page in the same URL update; Back/deep links keep their page. */
  function setParams(patch: Record<string, unknown>, resetPage = true) {
    void navigate({
      to: '.',
      replace: true,
      resetScroll: false,
      search: ((previous: Record<string, unknown>) => {
        const next = { ...previous, ...patch };
        if (resetPage) delete next.page;
        for (const [key, value] of Object.entries(next))
          if (value === undefined || value === '') delete next[key];
        return next;
      }) as never,
    });
  }
  const query: ListInput = {
    search: settledSearch.trim() || undefined,
    sort: sort?.key,
    dir: sort?.dir,
    filters: Object.keys(filters).length ? filters : undefined,
    limit: pageSize,
    offset: effectivePage * pageSize,
  };
  function table(total: number, loading?: boolean): ServerTable {
    return {
      total,
      loading,
      page: effectivePage,
      pageSize,
      sort,
      filters: state.filters ?? {},
      onPage: (next) => setParams({ page: next || undefined }, false),
      onSort: (next) => setParams({ sort: next?.key, dir: next?.dir }),
      onFilters: (next) => setParams({ filters: next }),
    };
  }
  return {
    search,
    setSearch: (next: string) => setParams({ search: next }),
    query,
    table,
    params,
    setParams,
  };
}
